// pollQuestionTone.js — auto-contrast pick for the Poll block's question text
// against the enclosing Segment's card background.
//
// Gordon 2026-09-15 02:05 PT (web-dev /builder/4908, screenshot): the
// rendered question ("What should I do with XYZ?") is hardcoded navy ink
// (#123051) regardless of what the Segment card is painted with — over an
// image-fill segment that is dark-on-dark, effectively invisible.
//
// Same rule as the app's existing "blocks default to segment colour +
// auto-contrast" family: a sampleable background (solid / gradient — see
// utils/segmentEmptyStateInk's resolveSegmentBackdrop) picks whichever of
// navy ink / white has the higher WCAG contrast (utils/contrast's
// autoTextColor) — on the overwhelmingly common plain-white segment this
// still picks navy ink, so the common case stays byte-identical. An
// unsampleable background (image / pattern with no overlay) falls back to
// white text with a soft scrim, mirroring TitleBar's own established
// convention for the same unsampleable case (titleBarLogic.js SCRIM_SHADOW).
//
// Gordon 2026-09-23 QA sweep (gordon-0923-P0-full-e2e-editwheel-delta-A-blocks-and-data,
// BUG-REGISTER.md, case 104): a segment with NO wallpaper configured at all
// (settings missing/empty — before the creator has touched the wallpaper
// picker) was ALSO falling into the "unsampleable" branch above and getting
// the white+scrim fallback, but that segment's actual card is the plain
// white/transparent default panel, not a dark image — white-on-white,
// illegible. resolveSegmentBackdrop can't make this distinction (it only
// sees currentBg/segmentBgColor and returns null either way), so this file
// checks "was a wallpaper configured at all" itself, BEFORE ever calling
// resolveSegmentBackdrop: no currentBg and no segmentBgColor on the settings
// object means nothing has been configured yet, so treat it as the known
// white default panel (navy ink) rather than an unsampleable one (white +
// scrim) — a wallpaper that IS configured but still unsampleable (image,
// pattern with no overlay) keeps the original white+scrim fallback.
import { autoTextColor, effectiveBgHex, INK_TEXT, WHITE_TEXT } from "../../../../utils/contrast";
import { resolveSegmentBackdrop } from "../../../../utils/segmentEmptyStateInk";

export const QUESTION_INK = INK_TEXT;
export const QUESTION_LIGHT = WHITE_TEXT;
export const QUESTION_SCRIM = "0 1px 3px rgba(18,48,81,0.55)";

/**
 * Has this Segment's settings recorded ANY wallpaper choice at all (solid /
 * pattern / image / gradient)? False for the default state of a brand new
 * segment whose settings haven't been touched yet.
 * @param {{currentBg?: number, segmentBgColor?: string}} [segmentSettings]
 * @returns {boolean}
 */
function isWallpaperConfigured(segmentSettings) {
  if (!segmentSettings) return false;
  const { currentBg, segmentBgColor } = segmentSettings;
  return currentBg !== undefined && currentBg !== null
    ? true
    : segmentBgColor !== undefined && segmentBgColor !== null;
}

/**
 * @param {{currentBg?: number, segmentBgColor?: string, wallpaperOverlayColor?: string}} [segmentSettings]
 * @returns {{color: string, textShadow: string|undefined}}
 */
export function pollQuestionTone(segmentSettings) {
  if (!isWallpaperConfigured(segmentSettings)) {
    return { color: QUESTION_INK, textShadow: undefined };
  }
  const hex = effectiveBgHex(resolveSegmentBackdrop(segmentSettings || {}));
  if (!hex) {
    return { color: QUESTION_LIGHT, textShadow: QUESTION_SCRIM };
  }
  return {
    color: autoTextColor(hex, { dark: QUESTION_INK, light: QUESTION_LIGHT, fallback: QUESTION_LIGHT }),
    textShadow: undefined,
  };
}

export default pollQuestionTone;
